import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import {
  Alert,
  Button,
  Dialog,
  FilterBar,
  PageHeader,
  Select,
  Textarea,
} from '@aaimplatform/ui';
import type { ActiveFilter, ColumnDef, FilterDefinition } from '@aaimplatform/ui';
import {
  RefreshCw as RefreshIcon,
  Plus as AddIcon,
} from 'lucide-react';
import { useFilings, useCreateFiling, useDebtors, useJurisdictions, useSecuredParties } from '../../hooks';
import type { CreateFilingRequest } from '../../hooks/useUccFilings';
import type { components as EntityComponents } from '@aaimplatform/institutional-types/ucc-entities';
import { formatDate } from '../../utils/display-format';
import {
  CodeBadge,
  FILING_STATUS_LABEL,
  FILING_STATUS_VARIANT,
  FILING_TYPE_LABEL,
  FilingNumberLink,
  labelFor,
  optionsFrom,
  UccPortfolioMetrics,
  UccTable,
  UccTenantScope,
  type FilingStatus,
  type FilingType,
  type UccFiling,
} from './ucc-display';

type SecuredParty = EntityComponents['schemas']['SecuredParty'];

const DEFAULT_PAGE_SIZE = 20;

/** Page sizes the rows-per-page control offers; listFilings caps `limit` at 100. */
const PAGE_SIZE_OPTIONS = [20, 50, 100];

/**
 * Rows the create dialog requests for its secured-party and debtor lists, the
 * most GET /secured-parties and GET /debtors return in one page.
 */
export const ENTITY_OPTION_LIMIT = 100;

/**
 * Filter definitions, derived from the generated FilingStatus and FilingType
 * unions through the label maps in ucc-display.tsx. The hand-listed options
 * offered `filed` and `pending_submission`, which the canonical FilingStatus
 * enum does not contain (the backend rejects them), and omitted `active`,
 * `amended`, `continued` and `imported`.
 */
export const FILING_FILTERS: FilterDefinition[] = [
  { id: 'status', label: 'Status', type: 'select', options: optionsFrom(FILING_STATUS_LABEL) },
  { id: 'filingType', label: 'Filing type', type: 'select', options: optionsFrom(FILING_TYPE_LABEL) },
];

const FILING_TYPE_OPTIONS = optionsFrom(FILING_TYPE_LABEL);

function useFilingColumns(): ColumnDef<UccFiling, unknown>[] {
  return useMemo<ColumnDef<UccFiling, unknown>[]>(
    () => [
      {
        accessorKey: 'filingNumber',
        header: 'Filing #',
        cell: ({ row }) => <FilingNumberLink filing={row.original} />,
      },
      {
        accessorKey: 'filingType',
        header: 'Type',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-primary">
            {labelFor(FILING_TYPE_LABEL, row.original.filingType)}
          </span>
        ),
      },
      { accessorKey: 'jurisdictionCode', header: 'Jurisdiction' },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <CodeBadge code={row.original.status} labels={FILING_STATUS_LABEL} variants={FILING_STATUS_VARIANT} />
        ),
      },
      {
        accessorKey: 'filingDate',
        header: 'Filed',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">{formatDate(row.original.filingDate)}</span>
        ),
      },
      {
        accessorKey: 'expirationDate',
        header: 'Expires',
        cell: ({ row }) => (
          <span className="text-sm font-sans text-secondary">{formatDate(row.original.expirationDate)}</span>
        ),
      },
    ],
    [],
  );
}

const EMPTY_FORM = {
  filingType: 'ucc1' as FilingType,
  jurisdictionCode: '',
  securedPartyId: '',
  debtorId: '',
  collateralDescription: '',
};

/** "Acme Lending Corp, Wilmington, DE": the legal name and where the entity is. */
export function entityOptionLabel(entity: Pick<SecuredParty, 'legalName' | 'mailingAddress'>): string {
  const place = [entity.mailingAddress.city, entity.mailingAddress.state].filter(Boolean).join(', ');
  return place ? `${entity.legalName}, ${place}` : entity.legalName;
}

/** Every field POST /ucc-filings requires, chosen or filled in. */
export function filingFormIsComplete(form: typeof EMPTY_FORM): boolean {
  return Boolean(
    form.jurisdictionCode && form.securedPartyId && form.debtorId && form.collateralDescription.trim(),
  );
}

/**
 * The placeholder and helper line for an entity select, from its read state.
 * An empty list points to the Entities page, where secured parties and
 * debtors are created; a failed read says so instead of listing nothing.
 */
function entitySelectState(
  list: { isLoading: boolean; error: Error | null; data?: { data: unknown[]; count: number } },
  noun: { singular: string; plural: string },
): { placeholder: string; helperText?: string } {
  if (list.isLoading) return { placeholder: `Loading ${noun.plural}...` };
  if (list.error) {
    return { placeholder: `${noun.singular} list could not be read`, helperText: list.error.message };
  }
  const rows = list.data?.data.length ?? 0;
  if (rows === 0) {
    return {
      placeholder: `No ${noun.plural} for this tenant`,
      helperText: `Add one on the UCC Entities page, then return here.`,
    };
  }
  const total = list.data?.count ?? rows;
  return {
    placeholder: `Select a ${noun.singular.toLowerCase()}`,
    helperText: total > rows ? `The first ${rows} of ${total} ${noun.plural} are listed.` : undefined,
  };
}

export function UCCFilingListPage() {
  const tenantId = useAuthStore(s => s.user?.tenantId) ?? '';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  // The Dashboard's New Filing CTA lands here with ?new=1, so the dialog is
  // open on arrival instead of asking for a second click.
  const [createDialogOpen, setCreateDialogOpen] = useState(() => searchParams.get('new') === '1');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [newFilingForm, setNewFilingForm] = useState(EMPTY_FORM);
  const columns = useFilingColumns();

  const statusFilter = activeFilters.find((f) => f.filterId === 'status')?.value as FilingStatus | undefined;
  const typeFilter = activeFilters.find((f) => f.filterId === 'filingType')?.value as FilingType | undefined;

  const { data, isLoading, error, refetch } = useFilings({
    tenantId,
    status: statusFilter,
    filingType: typeFilter,
    page,
    limit: pageSize,
  });

  const createFiling = useCreateFiling();
  // The dialog's lists are read only while it is open.
  const securedParties = useSecuredParties({ tenantId, limit: ENTITY_OPTION_LIMIT, enabled: createDialogOpen });
  const debtors = useDebtors({ tenantId, limit: ENTITY_OPTION_LIMIT, enabled: createDialogOpen });
  const jurisdictions = useJurisdictions();

  const securedPartyOptions = (securedParties.data?.data ?? []).map((sp) => ({
    value: sp.securedPartyId,
    label: entityOptionLabel(sp),
  }));
  const debtorOptions = (debtors.data?.data ?? []).map((d) => ({ value: d.debtorId, label: entityOptionLabel(d) }));
  const jurisdictionOptions = (jurisdictions.data?.data ?? []).map((j) => ({
    value: j.stateCode,
    label: `${j.stateName} (${j.stateCode})`,
  }));
  const securedPartyState = entitySelectState(securedParties, { singular: 'Secured party', plural: 'secured parties' });
  const debtorState = entitySelectState(debtors, { singular: 'Debtor', plural: 'debtors' });

  const filings = data?.data ?? [];
  const totalCount = data?.count ?? 0;
  const filtered = searchQuery.trim()
    ? filings.filter((f) => (f.filingNumber ?? '').toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : filings;
  const isFiltered = Boolean(searchQuery.trim() || statusFilter || typeFilter);

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    if (searchParams.has('new')) {
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  };

  const handleCreate = async () => {
    const body: CreateFilingRequest = {
      tenantId,
      filingType: newFilingForm.filingType,
      jurisdictionCode: newFilingForm.jurisdictionCode,
      securedPartyId: newFilingForm.securedPartyId,
      debtorId: newFilingForm.debtorId,
      collateralDescription: newFilingForm.collateralDescription.trim(),
    };
    try {
      await createFiling.mutateAsync(body);
      setNewFilingForm(EMPTY_FORM);
      closeCreateDialog();
      refetch();
    } catch {
      // The dialog renders createFiling.error.
    }
  };

  return (
    <div>
      <PageHeader
        description="Filing lifecycle management"
        actions={
          <>
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading || !tenantId}>
              <RefreshIcon className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} size={16} />
              Refresh
            </Button>
            <Button variant="primary" onClick={() => setCreateDialogOpen(true)} disabled={!tenantId}>
              <AddIcon className="mr-2" size={16} />
              New Filing
            </Button>
          </>
        }
      />

      <UccTenantScope tenantId={tenantId} />

      {tenantId && (
        <>
          <UccPortfolioMetrics tenantId={tenantId} />

          <FilterBar
            filters={FILING_FILTERS}
            activeFilters={activeFilters}
            onFiltersChange={(next) => { setActiveFilters(next); setPage(1); }}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search this page by filing number"
            className="rounded-t-sm"
          />

          <UccTable<UccFiling>
            columns={columns}
            rows={filtered}
            isLoading={isLoading}
            error={error}
            errorTitle="Could not load UCC filings"
            onRetry={() => refetch()}
            empty={
              isFiltered
                ? { title: 'No filings match the current filters', description: 'Clear a filter or the search to widen the list.' }
                : {
                    title: 'No UCC filings for this tenant yet',
                    description:
                      'Filings appear here once a pledge is perfected or an operator creates one with New Filing.',
                  }
            }
            getRowId={(row) => row.filingId}
            onRowClick={(row) => navigate(`/ucc/filings/${row.filingId}`)}
            className="rounded-b-sm shadow-sm"
            pagination={{
              page: page - 1,
              pageSize,
              total: totalCount,
              onPageChange: (index) => setPage(index + 1),
              onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
            }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </>
      )}

      <Dialog
        open={createDialogOpen}
        onOpenChange={(open) => { if (!open) closeCreateDialog(); }}
        title="Create New UCC Filing"
        size="md"
        footer={
          <>
            <Button variant="tertiary" onClick={closeCreateDialog} disabled={createFiling.isPending}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={createFiling.isPending || !tenantId || !filingFormIsComplete(newFilingForm)}
            >
              {createFiling.isPending ? 'Creating...' : 'Create Filing'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {createFiling.error && (
            <Alert type="error" title="The filing was not created">
              {createFiling.error.message}
            </Alert>
          )}
          <Select
            label="Filing Type"
            options={FILING_TYPE_OPTIONS}
            value={newFilingForm.filingType}
            onChange={(e) => setNewFilingForm(prev => ({ ...prev, filingType: e.target.value as FilingType }))}
            required
          />
          <Select
            label="Jurisdiction"
            options={jurisdictionOptions}
            placeholder={jurisdictions.error ? 'Jurisdictions could not be read' : 'Select a jurisdiction'}
            helperText={jurisdictions.error?.message}
            value={newFilingForm.jurisdictionCode}
            onChange={(e) => setNewFilingForm(prev => ({ ...prev, jurisdictionCode: e.target.value }))}
            required
          />
          <Select
            label="Secured Party"
            options={securedPartyOptions}
            placeholder={securedPartyState.placeholder}
            helperText={securedPartyState.helperText}
            value={newFilingForm.securedPartyId}
            onChange={(e) => setNewFilingForm(prev => ({ ...prev, securedPartyId: e.target.value }))}
            required
          />
          <Select
            label="Debtor"
            options={debtorOptions}
            placeholder={debtorState.placeholder}
            helperText={debtorState.helperText}
            value={newFilingForm.debtorId}
            onChange={(e) => setNewFilingForm(prev => ({ ...prev, debtorId: e.target.value }))}
            required
          />
          <Textarea
            label="Collateral Description"
            placeholder="All assets and personal property..."
            value={newFilingForm.collateralDescription}
            onChange={(v) => setNewFilingForm(prev => ({ ...prev, collateralDescription: v }))}
            required
          />
        </div>
      </Dialog>
    </div>
  );
}
